import React from 'react';

import {
  AnalysisTemplateSelectorTemplate,
  useAnalysisTemplatesWithFilter,
} from 'analysis-tool/graphql/AnalysisTemplateSelectorQuery';
import SelectDropdown, {SelectDropdownErrorStyle} from 'ui-library/dropdowns/SelectDropdown';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';
import Menu from 'ui-library/menus/Menu';
import SearchableMenu from 'ui-library/menus/SearchableMenu';

export interface SimpleAnalysisTemplateSelectorProps {
  /** The selected analysis template */
  template: Pick<AnalysisTemplateSelectorTemplate, 'id'> | null;
  /** Callback when a template is selected */
  onSelectTemplate?: (selectedTemplate: AnalysisTemplateSelectorTemplate | null) => void;
  /** Optional filter, to limit which templates are shown in the dropdown */
  filterTemplate?: (template: AnalysisTemplateSelectorTemplate) => boolean;
  /** True, to allow selecting 'None', instead of a template */
  includeNone?: boolean;
  /** Custom dropdown toggle component. */
  dropdownToggleOverride?: JSX.Element;
  disabled?: boolean;
  /** Optional disabled tooltip message to display when disabled */
  disabledTooltip?: string | null;
}

/**
 * A simpler selector for analysis templates which doesn't depend on `useAnalysisTemplateSelectorState`or load
 * the entire `AnalysisTemplateGraphQL` for the selected template.
 *
 * This component is appropriate if the metadata in `AnalysisTemplateSelectorTemplate` is sufficient.
 */
export default function SimpleAnalysisTemplateSelector({
  template,
  onSelectTemplate,
  filterTemplate,
  includeNone,
  dropdownToggleOverride,
  disabled,
  disabledTooltip,
}: SimpleAnalysisTemplateSelectorProps): JSX.Element {
  const {loading, allTemplates, validTemplates} = useAnalysisTemplatesWithFilter({
    filterTemplates: filterTemplate,
  });

  if (loading) {
    return <CenteredHorizontalThrobber />;
  }

  const selectedTemplate = allTemplates.find(({id}) => id === template?.id);
  const optionDatas = validTemplates.map((template) => ({
    name: template.name,
    value: template,
    dataTestAttrs: {
      component: 'AnalysisTemplateSelector',
      element: 'option',
      key: template.id,
    },
  }));

  return (
    <div role="menu" aria-label="Template selector">
      <SelectDropdown
        menu={
          <SearchableMenu
            onSelect={onSelectTemplate || (() => {})}
            optionDatas={includeNone ? [{name: 'None', value: null}, ...optionDatas] : optionDatas}
            selectedValue={selectedTemplate}
            width={Menu.WIDTHS.FULL}
          />
        }
        displayName={selectedTemplate?.name ?? 'Select template...'}
        dropdownToggleOverride={dropdownToggleOverride}
        disabled={disabled}
        shouldGrowDisplayName={true}
        error={disabled ? disabledTooltip : undefined}
        errorStyle={disabled ? SelectDropdownErrorStyle.TOOLTIP : undefined}
        dataTestAttrs={{component: 'AnalysisTemplateSelector', element: 'root'}}
      />
    </div>
  );
}
